import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { LOCATIONS, ASSETS } from "../../data/site";
import { trackEvent } from "../../lib/analytics";

export const OrderModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      trackEvent("order_online_click");
      setOpen(true);
    };
    window.addEventListener("rnr:order", handler);
    return () => window.removeEventListener("rnr:order", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const startOrder = (loc) => {
    trackEvent("location_selected", { location: loc.city });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="order-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#2A1620]/60 backdrop-blur-sm">
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-[2rem] bg-[#FFF7F0] shadow-[0_40px_90px_-30px_rgba(246,60,139,0.6)]">
            {/* header */}
            <div className="relative bg-[#F63C8B] text-white px-6 sm:px-8 py-6">
              <button data-testid="order-modal-close" onClick={() => setOpen(false)} aria-label="Close"
                className="absolute top-4 right-4 grid place-items-center h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                <X size={18} />
              </button>
              <div className="flex items-center gap-3">
                <img src={ASSETS.logoWhite} alt="" aria-hidden="true" className="h-12 w-12 object-contain" />
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl leading-none">Choose Your Location</h3>
                  <p className="text-white/85 text-sm mt-1">Order online for pickup — powered by Clover</p>
                </div>
              </div>
            </div>

            {/* list */}
            <div className="px-4 sm:px-6 py-5 overflow-y-auto max-h-[calc(88vh-104px)]">
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {LOCATIONS.map((loc, i) => (
                  <div key={loc.city} data-testid={`order-loc-${i}`}
                    className="card p-5 flex flex-col">
                    <h4 className="font-display text-lg text-[#2A1620]">Rock N Roll {loc.city}</h4>
                    <p className="mt-2 text-sm text-[#7C6A72] flex items-start gap-2"><MapPin size={15} className="text-[#F63C8B] mt-0.5 shrink-0" /> {loc.address}</p>
                    <p className="mt-1.5 text-sm text-[#7C6A72] flex items-center gap-2"><Clock size={15} className="text-[#12B99B] shrink-0" /> {loc.hours}</p>
                    <a href={loc.order} target="_blank" rel="noopener noreferrer"
                      onClick={() => startOrder(loc)} data-testid={`order-start-${i}`}
                      className="btn-primary mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em]">
                      Start Order <ArrowUpRight size={15} />
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-center text-xs text-[#B79AA6] mt-5">Secure ordering & payment handled by Clover. Orders go straight to the store.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
